import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './exportMenu.css';

/** One row in an {@link ActionMenu}.
 *
 *  A row is an ACTION by default. `kind: 'header'` renders its label as a
 *  small group caption and `kind: 'separator'` a hairline rule; both are inert
 *  — the arrows skip them, they never run, and hovering does not select them. */
export type ActionMenuItem = {
  /** Stable key. */
  id: string;
  /** Row caption. Unused by a separator. */
  label: string;
  /** What this row IS. Defaults to an action. */
  kind?: 'item' | 'header' | 'separator';
  /** Tabler icon class, e.g. `ti-refresh`. */
  icon?: string;
  /** Greyed + not selectable when true. */
  disabled?: boolean;
  /** Effective keyboard combo to display on the right (rule 14 discoverability). */
  hotkey?: string;
  /** Runs on click / Enter / Space. */
  onSelect: () => void;
};

export type ActionMenuProps = {
  /** The rows, top to bottom. */
  items: ActionMenuItem[];
  /** Trigger caption; omit for the icon-only overflow button. */
  label?: string;
  /** Trigger icon. Omit for the plain `⋯` overflow glyph, which is a literal
   *  character rather than an icon-font class — the prototype's own trigger,
   *  and it cannot render blank if the icon set lacks that glyph. */
  icon?: string;
  /** Trigger tooltip / aria-label. */
  title?: string;
  /** Trigger class — defaults to the shared toolbar button style. */
  triggerClassName?: string;
  /** Disables the trigger. */
  disabled?: boolean;
};

const inert = (it?: ActionMenuItem) => !it || !!it.disabled || (!!it.kind && it.kind !== 'item');

const GAP = 4;
const EDGE = 8;

/**
 * The ONE shared popup ACTION menu (the toolbar overflow button and any other
 * "menu of actions" trigger). Shares the placement, styling and keyboard
 * contract of {@link ExportMenu} — standing rule 8 (never reinvent) and
 * rule 14: while open a WINDOW capture-phase keydown drives it, so the arrows
 * move the cursor, Home/End jump, Enter/Space run the cursor row and Esc
 * closes and returns focus to the trigger — and the keys never leak to the
 * grid behind the menu.
 */
export function ActionMenu({
  items,
  label,
  icon,
  title = 'More actions',
  triggerClassName = 'ca-btn',
  disabled,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; maxH: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Live mirrors for the window handler, which closes over the first render.
  const activeRef = useRef(0);
  activeRef.current = active;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const close = useCallback(() => { setOpen(false); btnRef.current?.focus(); }, []);

  const run = useCallback((i: number) => {
    const it = itemsRef.current[i];
    if (!it || it.disabled || (it.kind && it.kind !== 'item')) return;
    setOpen(false);
    it.onSelect();
  }, []);

  // Fixed-position placement, clamped inside the viewport on every edge, so the
  // menu is never clipped even when the trigger is scrolled near an edge.
  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = menuRef.current;
    const mw = m?.offsetWidth || 200;
    const mh = m?.scrollHeight || 200;
    const below = vh - b.bottom - GAP - EDGE;
    const above = b.top - GAP - EDGE;
    let top: number;
    let maxH: number;
    if (mh <= below || below >= above) { top = b.bottom + GAP; maxH = below; }
    else { maxH = above; top = b.top - GAP - Math.min(mh, above); }
    maxH = Math.max(120, Math.min(maxH, vh - EDGE * 2));
    const h = Math.min(mh, maxH);
    top = Math.max(EDGE, Math.min(top, vh - EDGE - h));
    const left = Math.min(Math.max(EDGE, b.right - mw), Math.max(EDGE, vw - mw - EDGE));
    setPos({ top, left, maxH });
  }, []);

  useLayoutEffect(() => { if (!open) { setPos(null); return; } place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const h = () => place();
    window.addEventListener('scroll', h, true);
    window.addEventListener('resize', h);
    return () => { window.removeEventListener('scroll', h, true); window.removeEventListener('resize', h); };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Open on the first SELECTABLE row (not disabled, and not a group header or
  // separator), move focus into the menu once it is placed, and keep the cursor
  // row scrolled into view.
  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, itemsRef.current.findIndex((i) => !inert(i))));
  }, [open]);
  useEffect(() => { if (open && pos) menuRef.current?.focus(); }, [open, pos]);
  useEffect(() => {
    if (!open || !pos) return;
    menuRef.current?.querySelector('.acc-export-item.active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, pos]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const list = itemsRef.current;
      const n = list.length;
      if (!n) return;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      // Step over disabled rows so the cursor only ever rests on a runnable one.
      const step = (dir: 1 | -1) => setActive((a) => {
        for (let k = 1; k <= n; k++) {
          const i = ((a + dir * k) % n + n) % n;
          if (!inert(list[i])) return i;
        }
        return a;
      });
      const edge = (from: 'start' | 'end') => setActive((a) => {
        const order = list.map((_, i) => (from === 'start' ? i : n - 1 - i));
        return order.find((i) => !inert(list[i])) ?? a;
      });
      if (e.key === 'ArrowDown') { stop(); step(1); }
      else if (e.key === 'ArrowUp') { stop(); step(-1); }
      else if (e.key === 'Home') { stop(); edge('start'); }
      else if (e.key === 'End') { stop(); edge('end'); }
      else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { stop(); run(activeRef.current); }
      else if (e.key === 'Escape') { stop(); close(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, run, close]);

  return (
    <div className="acc-export" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className={triggerClassName}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {icon
          ? <i className={`ti ${icon}`} />
          : <span className="acc-more-glyph" aria-hidden="true">⋯</span>}
        {label ? <> {label} ▾</> : null}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="acc-export-menu acc-export-menu-fixed"
          role="menu"
          aria-label={title}
          tabIndex={-1}
          style={pos ? { top: pos.top, left: pos.left, maxHeight: pos.maxH } : { visibility: 'hidden' }}
        >
          {items.map((it, i) => (
            it.kind === 'separator' ? (
              <div key={it.id} className="acc-export-sep" role="separator" />
            ) : it.kind === 'header' ? (
              <div key={it.id} className="acc-export-head" role="presentation">{it.label}</div>
            ) : (
            <div
              key={it.id}
              role="menuitem"
              tabIndex={-1}
              aria-disabled={it.disabled || undefined}
              className={`acc-export-item${i === active ? ' active' : ''}${it.disabled ? ' disabled' : ''}`}
              onClick={() => run(i)}
              onMouseEnter={() => { if (!it.disabled) setActive(i); }}
            >
              {it.icon ? <i className={`ti ${it.icon}`} /> : null} {it.label}
              {it.hotkey ? <span className="acc-export-key">{it.hotkey}</span> : null}
            </div>
            )
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
